/**
 * PlacementSchadenverlaufRef — the read-only Schadenverlauf reference on Ausschreibungs-Screen Tab 1
 * (P2 Placement Slice 6, Ch 7.3 «schreibgeschützt angezeigt»). Tab 1 never triggers a retrieval; it
 * reflects the Referenzpolice's current entry (via SchadenverlaufCard) or a reason:
 *   available       → the entry + a «Zur Police» link (retrieval happens on the Police)
 *   neugeschaeft    → the muted "reines Neugeschaeft" sentence (no Referenzpolice)
 *   nicht_eingeholt → an amber hint + «Auf der Police einholen» link
 *   permission      → a muted "keine Berechtigung" sentence
 *
 * Keeps the Slice-5 `plt1-schadenverlauf` test id. `policyId` (the placement's Referenzpolice, from
 * the snapshot response) backs the `nicht_eingeholt` «Auf der Police einholen» link, since that ref
 * variant carries no id of its own (the `available` variant uses its own `policy_id`).
 */

import { useNavigate } from '@tanstack/react-router';
import { SCHADENVERLAUF_NICHT_EINGEHOLT_HINWEIS } from '@brokly/shared';
import type { PlacementSchadenverlaufRef as PlacementSchadenverlaufRefType } from '@brokly/shared';
import { EntityLink } from '../../components/ui/EntityLink';
import { SchadenverlaufCard } from '../entity-detail/components/police-risiko/SchadenverlaufCard';

export function PlacementSchadenverlaufRef({
  ref,
  policyId,
}: {
  ref: PlacementSchadenverlaufRefType;
  policyId?: string | null;
}) {
  const navigate = useNavigate();

  if (ref.available) {
    const toPolice = () =>
      void navigate({ to: '/policen/$id', params: { id: ref.policy_id }, search: { tab: 'risiko' } });
    return (
      <section data-testid="plt1-schadenverlauf" className="space-y-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3 className="text-sm font-semibold text-foreground">
            Schadenverlauf (Referenzpolice, schreibgeschützt)
          </h3>
          <EntityLink onActivate={toPolice}>Zur Police</EntityLink>
        </div>
        {/* Always the card — it shows the E17 «leer» banner itself (contract §5 /
            SchadenverlaufCard), so Quelle/Bezugsdatum/Periode/Demo-chip stay visible. */}
        <SchadenverlaufCard eintrag={ref.eintrag} />
      </section>
    );
  }

  return (
    <section
      data-testid="plt1-schadenverlauf"
      className="rounded-lg border border-dashed bg-muted/20 p-4 text-sm text-muted-foreground"
    >
      {ref.reason === 'neugeschaeft' && 'Schadenverlauf: reines Neugeschaeft (kein Verlauf).'}
      {ref.reason === 'nicht_eingeholt' && (
        <div className="space-y-1">
          <p className="text-status-warning">{SCHADENVERLAUF_NICHT_EINGEHOLT_HINWEIS}</p>
          {policyId && (
            <EntityLink
              onActivate={() =>
                void navigate({ to: '/policen/$id', params: { id: policyId }, search: { tab: 'risiko' } })
              }
            >
              Auf der Police einholen
            </EntityLink>
          )}
        </div>
      )}
      {ref.reason === 'permission' && 'Keine Berechtigung für den Schadenverlauf.'}
    </section>
  );
}
